import { ChangeEvent, useCallback } from "react";

import { MeetingOrTemplateVisibility } from "@apps/meeting-new/helpers";
import { currentOrganizationVar } from "@cache/cache";

const MeetingContentAccess = ({
  onChange,
  allowManagementTreeVisibility,
  allowOrgVisibility,
  allowAdminVisibility,
  isFormalOneonone,
}: {
  isFormalOneonone: boolean;
  onChange: ({
    allowAdminVisibility,
    allowOrgVisibility,
    allowManagementTreeVisibility,
  }: MeetingOrTemplateVisibility) => void;
} & MeetingOrTemplateVisibility) => {
  const organization = currentOrganizationVar();

  const handleToggleAdminVisibility = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange({
        allowManagementTreeVisibility,
        allowOrgVisibility,
        allowAdminVisibility: e.target.checked,
      });
    },
    [allowManagementTreeVisibility, allowOrgVisibility, onChange],
  );

  const handleToggleOrgVisibility = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newAllowOrgVisibility = e.target.checked;
      onChange({
        allowOrgVisibility: newAllowOrgVisibility,
        allowAdminVisibility: newAllowOrgVisibility
          ? true
          : allowAdminVisibility,
        allowManagementTreeVisibility: newAllowOrgVisibility
          ? true
          : allowManagementTreeVisibility,
      });
    },
    [onChange, allowAdminVisibility, allowManagementTreeVisibility],
  );

  const handleToggleManagementTreeVisibility = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange({
        allowAdminVisibility,
        allowOrgVisibility,
        allowManagementTreeVisibility: e.target.checked,
      });
    },
    [allowAdminVisibility, allowOrgVisibility, onChange],
  );

  return (
    <div className="flex flex-col gap-1 text-gray-800 text-sm">
      <label className="flex gap-3 items-start rounded-sm px-3 py-2">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          name="participants"
          checked={true}
          disabled
        />
        <div className="flex flex-col gap-1">
          <div className="font-medium">Private (Participants only)</div>
          <div className="text-gray-500">
            Only meeting participants can view the meeting content and the
            content <i>won't</i> be used in work history.
          </div>
        </div>
      </label>
      <label className="flex gap-3 items-start cursor-pointer hover:bg-gray-50 rounded-sm px-3 py-2">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          name="allowManagementTreeVisibility"
          checked={allowManagementTreeVisibility}
          onChange={handleToggleManagementTreeVisibility}
          disabled={allowOrgVisibility}
        />
        <div className="flex flex-col gap-1">
          <div className="font-medium">Management Tree</div>
          <div className="text-gray-500">
            All upstream managers of the meeting participants can view the
            meeting content. The content will be used in work history.
          </div>
        </div>
      </label>
      <label className="flex gap-3 items-start cursor-pointer hover:bg-gray-50 rounded-sm px-3 py-2">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          name="allowAdminVisibility"
          checked={allowAdminVisibility}
          onChange={handleToggleAdminVisibility}
          disabled={allowOrgVisibility}
        />
        <div className="flex flex-col gap-1">
          <div className="font-medium">Admins</div>
          <div className="text-gray-500">
            All administrators can view the meeting content. The content will be
            used in work history.
          </div>
        </div>
      </label>
      <label className="flex gap-3 items-start cursor-pointer hover:bg-gray-50 rounded-sm px-3 py-2">
        <input
          type="checkbox"
          className="mt-1 shrink-0"
          name="allowOrgVisibility"
          checked={allowOrgVisibility}
          onChange={handleToggleOrgVisibility}
          disabled={isFormalOneonone}
        />
        <div className="flex flex-col gap-1">
          <div className="font-medium">
            Open (All {organization.name} Members)
          </div>
          <div className="text-gray-500">
            Everyone at {organization.name} can view the meeting content and the
            content <i>will</i> be used in work history.
          </div>
        </div>
      </label>
    </div>
  );
};

export default MeetingContentAccess;
